import { useCallback, useEffect, useRef, useState } from "react";
import {
  deepMerge,
  getErrorMessage,
  normalizeEn,
  setAtPath,
} from "utils/cms";

export default function useCmsPage({
  load,
  create,
  update,
  remove,
  empty,
  mapResponse,
  prepare,
}) {
  const [form, setForm] = useState(empty);
  const [savedForm, setSavedForm] = useState(empty);
  const [frMap, setFrMap] = useState({});
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState("");
  const [toast, setToast] = useState(null);
  const optionsRef = useRef({
    load,
    create,
    update,
    remove,
    empty,
    mapResponse,
    prepare,
  });
  optionsRef.current = {
    load,
    create,
    update,
    remove,
    empty,
    mapResponse,
    prepare,
  };

  const showToast = (type, message) =>
    setToast({ type, message, id: Date.now() });

  const refresh = useCallback(async ({ silent } = {}) => {
    const { load: loadFn, mapResponse: mapFn, empty: emptyForm } =
      optionsRef.current;
    if (!silent) setLoading(true);
    try {
      const res = await loadFn();
      const payload = mapFn(res);
      if (!payload) {
        setExists(false);
        setForm(emptyForm);
        setSavedForm(emptyForm);
        setFrMap({});
      } else {
        const fr = {};
        const normalized = normalizeEn(payload, "", fr);
        const nextForm = deepMerge(emptyForm, normalized);
        setExists(true);
        setForm(nextForm);
        setSavedForm(nextForm);
        setFrMap(fr);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setExists(false);
        setForm(optionsRef.current.empty);
        setSavedForm(optionsRef.current.empty);
        setFrMap({});
      } else {
        showToast("error", getErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, load, empty]);

  const setField = (path, value) => {
    setForm((prev) => setAtPath(prev, path, value));
  };

  const isSectionDirty = (keys = []) =>
    keys.some(
      (key) => JSON.stringify(form[key]) !== JSON.stringify(savedForm[key])
    );

  const saveSection = async (sectionId, keys) => {
    const { update: updateFn, prepare: prepareFn } = optionsRef.current;
    setSavingSection(sectionId);
    try {
      const section = {};
      keys.forEach((key) => {
        if (form[key] !== undefined) section[key] = form[key];
      });
      const payload = prepareFn ? prepareFn(section) : section;
      const res = await updateFn(payload);
      showToast("success", res.data?.message || "Saved successfully");
      await refresh({ silent: true });
    } catch (error) {
      showToast("error", getErrorMessage(error));
    } finally {
      setSavingSection("");
    }
  };

  const save = async () => {
    const {
      create: createFn,
      update: updateFn,
      prepare: prepareFn,
    } = optionsRef.current;
    setSavingSection("all");
    try {
      const payload = prepareFn ? prepareFn(form) : form;
      const res = exists ? await updateFn(payload) : await createFn(payload);
      showToast("success", res.data?.message || "Saved successfully");
      await refresh({ silent: true });
    } catch (error) {
      showToast("error", getErrorMessage(error));
    } finally {
      setSavingSection("");
    }
  };

  const destroy = async () => {
    if (!window.confirm("Delete this content? This cannot be undone.")) return;
    setSavingSection("all");
    try {
      const res = await optionsRef.current.remove();
      showToast("success", res.data?.message || "Deleted successfully");
      setExists(false);
      setForm(optionsRef.current.empty);
      setSavedForm(optionsRef.current.empty);
      setFrMap({});
    } catch (error) {
      showToast("error", getErrorMessage(error));
    } finally {
      setSavingSection("");
    }
  };

  return {
    form,
    setForm,
    setField,
    frMap,
    exists,
    loading,
    saving: !!savingSection,
    savingSection,
    toast,
    setToast,
    save,
    saveSection,
    isSectionDirty,
    destroy,
    refresh,
  };
}
